import React, { PureComponent } from 'react'
import { func, string } from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { withI18n } from '@active/react-utils/i18n'
import ProxyModalWithBodyDesc, { VerifyCodeByNewPhone } from '../../../common/ProxyModalWithBodyDesc'
import { messageUtils } from '../../../../shared/utils/commonUtils'
import { calculateCounter } from '../../../common/MFAContent/MFAContent'
import './PhoneAuthentication.scss'

const PHONE_SETUP_VERIFY = 'PHONE_SETUP_VERIFY'
const PHONE_SETUP_SEND = 'PHONE_SETUP_SEND'
const PAGE_RENDER = 'PAGE_RENDER'
const PHONE_SETUP_SKIP = 'PHONE_SETUP_SKIP'
class PhoneAuthentication extends PureComponent {
  static propTypes = {
    onVerify: func.isRequired,
    configureTrack: func.isRequired,
    i18n: func.isRequired,
    nextTimeWindow: string,
    message: string, // for page render
    errorCode: string, // for page render
    renderModel: string, // for page render
    phoneNumber: string, // for page render
    epid: string,
    locale: string,
  }

  static defaultProps = {
    nextTimeWindow: undefined,
    message: undefined,
    errorCode: undefined,
    renderModel: undefined,
    phoneNumber: '',
    epid: '',
    locale: 'en_US',
  }

  interval = null

  constructor(props) {
    super(props)

    this.state = {
      counter: undefined,
      isFirstSendCode: !this.props.nextTimeWindow,
    }

    this.proxyForm = this.proxyForm.bind(this)
    this.proxyPhoneForm = this.proxyPhoneForm.bind(this)
  }

  componentDidMount() {
    const {
      epid, renderModel, nextTimeWindow, configureTrack, errorCode, message,
    } = this.props
    if (errorCode === 'PHONE_SETUP_VERIFY_FAILED' || errorCode === 'PHONE_SETUP_SENDCODE_FAILED') {
      message && messageUtils.alertError(message)
    }
    if (nextTimeWindow) {
      this.updateCounter({ nextTimeWindow })
      this.setSendCodeWording(nextTimeWindow)
    }
    configureTrack({
      version: 'v5',
      pageType: 'sign in|phone setup',
      mfaChannel: 'SMS',
      widget: renderModel === PAGE_RENDER ? '' : 'signIn_phone_setup',
      eventTracking: 'init',
      epid: epid,
    })
  }

  componentDidUpdate(prevProps) {
    const { nextTimeWindow } = this.props
    const { isFirstSendCode } = this.state
    if (nextTimeWindow && isFirstSendCode) {
      this.setState({ isFirstSendCode: false })
    }
    if (prevProps.nextTimeWindow !== nextTimeWindow && nextTimeWindow) {
      this.updateCounter({ nextTimeWindow })
      this.setSendCodeWording(nextTimeWindow)
    }
  }

  componentWillUnmount() {
    this.interval && clearInterval(this.interval)
  }

  setSendCodeWording = nextTimeWindow => {
    const counter = calculateCounter(nextTimeWindow)
    counter && this.setState({
      showSentCodeWording: true,
    })
  }

  handleVerificationRequestCode = () => {
    const {
      epid, onVerify, configureTrack, renderModel,
    } = this.props
    this.phoneForm.validateForm({ shouldTouchFields: true }).then(async res => {
      if (isEmpty(res)) {
        const { phoneNumber } = this.phoneForm.values
        configureTrack({
          version: 'v5',
          pageType: 'sign in|phone setup',
          mfaChannel: 'SMS',
          widget: renderModel === PAGE_RENDER ? '' : 'signIn_phone_setup',
          eventTracking: 'send',
          epid: epid,
        })
        const { message, errorCode, nextTimeWindow } = await onVerify({
          phoneNumber: phoneNumber.replace(/\s*/g, ''),
          operation: PHONE_SETUP_SEND,
        })
        errorCode === 'PHONE_SETUP_SENDCODE_FAILED' && message && messageUtils.alertError(message)
        if (nextTimeWindow) {
          this.updateCounter({ nextTimeWindow })
          this.setSendCodeWording(nextTimeWindow)
        }
      }
    })
  }

  handleVerificationVerify = async () => {
    const {
      epid, onVerify, configureTrack, renderModel,
    } = this.props
    const phoneResult = await this.phoneForm.validateForm({ shouldTouchFields: true })
    const codeResult = await this.form.validateForm({ shouldTouchFields: true })
    if (isEmpty(phoneResult) && isEmpty(codeResult)) {
      configureTrack({
        version: 'v5',
        pageType: 'sign in|phone setup',
        mfaChannel: 'SMS',
        widget: renderModel === PAGE_RENDER ? '' : 'signIn_phone_setup',
        eventTracking: 'setup',
        epid: epid,
      })
      const { phoneNumber } = this.phoneForm.values
      const { code } = this.form.values
      const { message, errorCode } = await onVerify({
        code,
        phoneNumber: phoneNumber.replace(/\s*/g, ''),
        operation: PHONE_SETUP_VERIFY,
      })
      errorCode === 'PHONE_SETUP_VERIFY_FAILED' && message && messageUtils.alertError(message)
    }
  }

  handleClose = async () => {
    const {
      onVerify, epid, configureTrack, renderModel,
    } = this.props
    configureTrack({
      version: 'v5',
      pageType: 'sign in|phone setup',
      mfaChannel: 'SMS',
      widget: renderModel === PAGE_RENDER ? '' : 'signIn_phone_setup',
      eventTracking: 'skip',
      epid: epid,
    })
    await onVerify({
      skip: true,
      operation: PHONE_SETUP_SKIP,
    })
  }

  proxyForm = form => { this.form = form }

  proxyPhoneForm = phoneForm => { this.phoneForm = phoneForm }

  updateCounter({ nextTimeWindow }) {
    const counter = calculateCounter(nextTimeWindow)
    if (counter) {
      this.interval && clearInterval(this.interval)
      this.setState({ counter })
      this.interval = setInterval(() => {
        const currentCounter = calculateCounter(nextTimeWindow)
        this.setState({ counter: currentCounter })
        if (!currentCounter) {
          clearInterval(this.interval)
        }
      }, 1000)
    }
  }

  render() {
    const { phoneNumber, i18n, locale } = this.props
    const { counter, showSentCodeWording, isFirstSendCode } = this.state
    const sendCodeButtonText = isFirstSendCode ? 'passport.common.btn.send' : 'passport.common.btn.send.newCode'

    return (
      <ProxyModalWithBodyDesc
        title={i18n('passport.verification.modal.title')}
        desc={i18n('passport.security.setting.change.phone.modal.description')}
        cancelText={i18n('passport.phoneAuthentication.modal.button.text.skip')}
        shown
        size="md"
        className="setup-phone-modal"
        onClose={this.handleClose}
        handleCancel={this.handleClose}
        handleSubmit={this.handleVerificationVerify}
      >
        <VerifyCodeByNewPhone
          showTrustDevice={false}
          phoneNumber={phoneNumber}
          countDownNumber={counter}
          showSentCodeWording={showSentCodeWording}
          sendCodeButtonText={sendCodeButtonText}
          proxyForm={this.proxyForm}
          proxyPhoneForm={this.proxyPhoneForm}
          onSendCode={this.handleVerificationRequestCode}
          locale={locale}
        />
      </ProxyModalWithBodyDesc>
    )
  }
}

export default withI18n(PhoneAuthentication)
